import { useQuery } from "@tanstack/react-query";

import { AppAccessType } from "@fitness-app/data-models/entities/Automation";
import { TraineeStatus } from "@fitness-app/data-models/entities/Trainee";

import { supabase } from "~/store/initializeStore";

/**
 * Cap on rows pulled into the tab, mirroring the plan/diet section. `count` is exact, so the badge
 * and the truncation note report the real size even when the list is cut.
 */
const UNASSIGNED_CLIENTS_LIMIT = 500;

/** Only what the table renders — the row links out to the profile for everything else. */
export interface UnassignedClient {
  id: string;
  firstName: string;
  lastName: string | null;
  email: string;
  createdAt: string;
  // Null under RLS for roles without a read on the product catalog (TRE-1636).
  productClient: { productId: string; product: { name: string } | null } | null;
}

/**
 * Full-access clients nobody is coaching (TRE-1848). Limited access is excluded on purpose: those
 * clients run on automation and are not meant to have a trainer, so listing them would bury the
 * handful of real cases under hundreds of rows that are already in their correct state. An empty
 * tab is the expected steady state here and means there is nothing to assign.
 *
 * The tenant boundary comes from RLS (clients.read = ALL over the member's tenant).
 */
export const useUnassignedClients = (enabled = true) => {
  const { data, isLoading } = useQuery({
    queryKey: ["unassignedFullAccessClients"],
    enabled,
    queryFn: async () => {
      const { data, error, count } = await supabase
        .from("trainee")
        .select("id, firstName, lastName, email, createdAt, productClient:productId(productId, product(name))", {
          count: "exact",
        })
        .eq("status", TraineeStatus.ACTIVE)
        .eq("type", AppAccessType.FullAccess)
        .is("assignedTrainerId", null)
        .order("createdAt", { ascending: false })
        .limit(UNASSIGNED_CLIENTS_LIMIT)
        .returns<UnassignedClient[]>();

      if (error) {
        throw new Error(error.message);
      }

      return { list: data ?? [], count: count ?? data?.length ?? 0 };
    },
  });

  return {
    clients: data?.list ?? [],
    count: data?.count ?? 0,
    isLoading,
  };
};
